import React, { useState, useEffect } from "react";
import { MdClose, MdSave } from "react-icons/md";
import toast from "react-hot-toast";
import { createSector, updateSector } from "api/sectors";
import {
  MdWarehouse,
  MdLocalFireDepartment,
  MdBuild,
  MdOutlineOpacity,
  MdScale,
  MdFactory,
  MdScience,
  MdAssessment,
} from "react-icons/md";

const SECONDARY = "#f27405";

const ICON_OPTIONS = [
  { value: "warehouse", label: "Warehouse / Receiving", Icon: MdWarehouse },
  { value: "steam", label: "Steam / Roasting", Icon: MdLocalFireDepartment },
  { value: "build", label: "Shelling", Icon: MdBuild },
  { value: "opacity", label: "Peeling", Icon: MdOutlineOpacity },
  { value: "scale", label: "Grading", Icon: MdScale },
  { value: "factory", label: "Factory", Icon: MdFactory },
  { value: "science", label: "Quality / Lab", Icon: MdScience },
  { value: "assessment", label: "Assessment", Icon: MdAssessment },
];

export default function AddNewSector({ open, onClose, onSuccess, editSector = null }) {
  const isEdit = Boolean(editSector?.id);
  const [form, setForm] = useState({
    name: "",
    description: "",
    iconKey: "warehouse",
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pre-populate form when editing
  useEffect(() => {
    if (open) {
      if (editSector) {
        setForm({
          name: editSector.name || "",
          description: editSector.description || "",
          iconKey: editSector.iconKey || editSector.icon_key || "warehouse",
        });
      } else {
        setForm({ name: "", description: "", iconKey: "warehouse" });
      }
      setErrors({});
      setSubmitError("");
    }
  }, [open, editSector]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    if (submitError) setSubmitError("");
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = "Sector name is required";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    setSubmitError("");
    try {
      if (isEdit) {
        // Update existing sector
        const result = await updateSector(editSector.id, {
          name: form.name.trim(),
          description: form.description.trim() || "",
          iconKey: form.iconKey,
        });
        const sector = result.data;
        const payload = {
          id: sector.id,
          name: sector.name,
          description: sector.description || null,
          iconKey: sector.icon_key || sector.iconKey || form.iconKey,
          created_at: sector.created_at,
          updated_at: sector.updated_at,
        };
        if (typeof onSuccess === "function") onSuccess(payload);
        toast.success(result.message || "Sector updated successfully");
      } else {
        // Create new sector
        const result = await createSector({
          name: form.name.trim(),
          description: form.description.trim() || "",
          iconKey: form.iconKey,
        });
        const sector = result.data;
        const payload = {
          id: sector.id,
          name: sector.name,
          description: sector.description || null,
          iconKey: sector.icon_key || sector.iconKey || form.iconKey,
          created_at: sector.created_at,
          updated_at: sector.updated_at,
        };
        if (typeof onSuccess === "function") onSuccess(payload);
        toast.success(result.message || "Sector created successfully");
      }
      setForm({ name: "", description: "", iconKey: "warehouse" });
      setErrors({});
      onClose();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || `Failed to ${isEdit ? "update" : "create"} sector`;
      setSubmitError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setForm({ name: "", description: "", iconKey: "warehouse" });
    setErrors({});
    setSubmitError("");
    onClose();
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4">
        <div
          className="relative w-full max-w-md rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 px-6 py-4">
            <h2 className="text-lg font-black text-slate-900 dark:text-white">
              {isEdit ? "Edit Sector" : "Add New Sector"}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="p-2 rounded-xl text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              aria-label="Close"
            >
              <MdClose className="text-xl" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            <div>
              <label
                htmlFor="add-sector-name"
                className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1.5"
              >
                Sector Name <span className="text-red-500">*</span>
              </label>
              <input
                id="add-sector-name"
                name="name"
                type="text"
                value={form.name}
                onChange={handleChange}
                placeholder="e.g. Raw Nut Receiving"
                className={`w-full rounded-xl border px-4 py-2.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-offset-0 focus:outline-none ${
                  errors.name
                    ? "border-red-500 focus:ring-red-500/30"
                    : "border-slate-200 dark:border-slate-700 focus:ring-[#0e76a8]/30"
                }`}
              />
              {errors.name && (
                <p className="mt-1 text-xs font-medium text-red-500">{errors.name}</p>
              )}
            </div>

            {submitError && (
              <p className="text-sm font-medium text-red-500 bg-red-50 dark:bg-red-900/20 dark:text-red-400 px-4 py-2 rounded-xl">
                {submitError}
              </p>
            )}

            <div>
              <label
                htmlFor="add-sector-description"
                className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1.5"
              >
                Description
              </label>
              <textarea
                id="add-sector-description"
                name="description"
                value={form.description}
                onChange={handleChange}
                placeholder="Brief description of this processing sector"
                rows={3}
                className="w-full rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-2.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-[#0e76a8]/30 focus:ring-offset-0 focus:outline-none resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                Icon
              </label>
              <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                {ICON_OPTIONS.map((opt) => {
                  const Icon = opt.Icon;
                  const isSelected = form.iconKey === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() =>
                        setForm((prev) => ({ ...prev, iconKey: opt.value }))
                      }
                      title={opt.label}
                      className={`flex items-center justify-center rounded-xl border-2 p-3 transition-all ${
                        isSelected
                          ? "border-[#f27405] bg-[#f27405]/10 text-[#f27405]"
                          : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600"
                      }`}
                    >
                      <Icon className="text-2xl" />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white shadow-lg hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ backgroundColor: SECONDARY }}
              >
                <MdSave className="text-lg" />
                {isSubmitting ? "Saving…" : isEdit ? "Update Sector" : "Save Sector"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
